import { useCallback, useState } from 'react';
import { modals } from '@mantine/modals';
import { Button, Flex, Text } from '@mantine/core';
import { Box } from '@mantine/core';
import WarningIcon from '@app/shared/ui-kit/icons/WarningIcon';
import { Trans } from 'react-i18next';
import { palette } from '@app/shared/ui-kit/colors';
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';

/**
 * Base prompt component, used by PromptConfirm and PromptDelete.
 */
export default function Prompt({
  modalId,
  i18nValues,
  onConfirm,
  color,
  i18nKey,
  children,
  closeImmediately = false,
}: {
  i18nKey?: string;
  i18nValues?: Record<string, any>;
  modalId: string;
  children?: React.ReactNode;
  color: keyof typeof palette;
  onConfirm?: () => any;
  closeImmediately?: boolean;
}) {
  const [isLoading, setIsLoading] = useState(false);

  const confirm = useCallback(async () => {
    if (closeImmediately || !onConfirm) {
      modals.close(modalId);
      onConfirm?.();
      return;
    }

    try {
      setIsLoading(true);
      await onConfirm();
      modals.close(modalId);
    } catch (error) {
      console.error('Error in prompt confirmation:', error);
      // Don't close the modal on error so user can retry or cancel
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm, modalId, closeImmediately]);

  /* ******************************** RENDERING ******************************* */
  return (
    <Flex direction="column">
      <Box bg={color} h="xs" w="100%" mb="lg"></Box>

      <Flex direction="column" align="center" gap="md" px="xl" ta="center">
        <WarningIcon color={palette[color]} size={64} />

        {children || (
          <Text size="lg" ta="center" fw={FONT_WEIGHT.bold} c={palette[color]}>
            <Trans
              i18nKey={i18nKey}
              values={i18nValues}
              components={{ strong: <strong /> }}
            />
          </Text>
        )}
      </Flex>

      <Flex direction="row" gap="lg" justify="center" p="lg">
        <Button
          variant="subtle"
          color="blackroomGrey"
          c={palette.blackroomDarkGray3}
          onClick={() => modals.close(modalId)}
          disabled={isLoading}
        >
          <Trans i18nKey="Common:prompts.cancel" />
        </Button>
        <Button color={color} onClick={confirm} loading={isLoading}>
          <Trans i18nKey="Common:prompts.confirm" />
        </Button>
      </Flex>
    </Flex>
  );
}
